import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Navbar from '../../navbar/Navbar';
import { Review } from '../type';
import { ShareReviewState, DEFAULT_COLORS, MAX_TEXT_LENGTH } from './types';
import TemplateSelector from './components/TemplateSelector';
import ColorPicker from './components/ColorPicker';
import TextEditor from './components/TextEditor';
import { getTemplateComponent } from './TemplateRegistry';
import { generateImageFromTemplate, blobToFile } from './services/ImageGenerationService';
import loader from '../../assets/loader.png';
import s3Object from '../../service/s3getObject';
import { formatReviewText, truncateName, truncateText } from '../utils/formatters';
import { selectUserMyId } from '../../selectors/userSelectors';

const ShareReviewPage: React.FC = () => {
    const previewContainerRef = useRef<HTMLDivElement>(null);
    const [previewScale, setPreviewScale] = useState(0.296);
    const navigate = useNavigate();
    const location = useLocation();
    const templateRef = useRef<HTMLDivElement>(null);
    const userMyId = useSelector(selectUserMyId);
    const review = location.state?.review as Review | undefined;
    const savedReviewState = location.state?.reviewState as ShareReviewState | undefined;

    const [userLogo, setUserLogo] = useState<string | undefined>(undefined);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isNameModified, setIsNameModified] = useState(!!savedReviewState);

    const truncateInitialText = (text: string): string => {
        if (text.length > MAX_TEXT_LENGTH) {
            return text.substring(0, MAX_TEXT_LENGTH - 3) + '...';
        }
        return text;
    };

    const [state, setState] = useState<ShareReviewState>(
        savedReviewState || {
            selectedTemplate: 'template1',
            reviewerName: review?.reviewer || '',
            reviewText: truncateInitialText(review?.text || ''),
            rating: review?.rating || '5',
            network: review?.network || 'google_my_business',
            color: DEFAULT_COLORS[0].value,
            showLogo: true,
            showSource: true,
        },
    );

    useEffect(() => {
        if (!review) {
            navigate('/reviews');
        }
    }, [review, navigate]);

    useEffect(() => {
        if (userMyId === undefined) {
            return;
        }

        void (async () => {
            try {
                const response = await axios.get(`/api/my-api-user-logo/${userMyId}`);
                setUserLogo(response?.data?.url_user_logo || undefined);
            } catch (error) {
                console.error('❌ Error fetching My-Api user info:', error);
                if (axios.isAxiosError(error)) {
                    console.error('Response data:', error.response?.data);
                    console.error('Response status:', error.response?.status);
                }
            }
        })();
    }, [userMyId]);

    useEffect(() => {
        const calculateScale = () => {
            if (previewContainerRef.current) {
                const containerWidth = previewContainerRef.current.offsetWidth;
                const scale = containerWidth / 1080;
                setPreviewScale(scale);
            }
        };

        calculateScale();
        window.addEventListener('resize', calculateScale);

        return () => window.removeEventListener('resize', calculateScale);
    }, []);

    if (!review) {
        return null;
    }

    const displayText = formatReviewText(truncateText(state.reviewText, MAX_TEXT_LENGTH));
    const displayName = isNameModified ? state.reviewerName : truncateName(state.reviewerName);

    const TemplateComponent = getTemplateComponent(state.selectedTemplate);

    const templateProps = {
        reviewerName: displayName,
        reviewText: displayText,
        rating: state.rating,
        network: state.network,
        color: state.color,
        logo: userLogo,
        showLogo: state.showLogo,
        showSource: state.showSource,
    };

    const handleGenerate = async () => {
        if (!templateRef.current) {
            setError("Erreur : impossible de générer l'image");
            return;
        }

        setIsGenerating(true);
        setError(null);

        try {
            const element = templateRef.current;
            const blob = await generateImageFromTemplate(element);

            if (!blob) {
                throw new Error("Impossible de générer l'image");
            }

            const file = blobToFile(blob, 'review-share.jpg');
            const formData = new FormData();

            formData.append('file', file);

            const config = {
                headers: {
                    'content-type': 'multipart/form-data',
                },
            };

            const uploadResponse = await axios.post('/api/upload', formData, config);
            const uploadedFileName = uploadResponse.data.url;

            const s3Url = s3Object(uploadedFileName);
            const mediaUrlWithoutExt = uploadedFileName.substring(
                0,
                uploadedFileName.lastIndexOf('.'),
            );

            navigate('/add_publication', {
                state: {
                    uploadedMedia: {
                        url: s3Url,
                        fileName: uploadedFileName,
                        mediaUrl: mediaUrlWithoutExt,
                        extension: 'jpg',
                    },
                    source: 'review-share',
                    reviewData: {
                        review: review,
                        reviewState: state,
                    },
                },
            });
        } catch (err) {
            console.error('Erreur lors de la génération:', err);
            setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="flex flex-1 relative w-full h-screen">
            <div className="flex-none relative min-w-64">
                <Navbar />
            </div>
            <div className="flex-1 relative">
                <div className="w-full px-[clamp(1.5rem,3vw,2.25rem)] py-10 inline-flex flex-col justify-start items-start gap-5">
                    <div className="self-stretch inline-flex justify-between items-center">
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                            <div className="justify-center text-[#393939] text-[32px] font-bold font-['Montserrat'] leading-[38.40px]">
                                Partage des Avis
                            </div>
                            <div className="self-stretch justify-center text-[#757575] text-[13px] font-normal font-['Montserrat'] leading-none">
                                Partagez vos avis sur vos réseaux sociaux
                            </div>
                        </div>
                    </div>

                    <div className="self-stretch inline-flex justify-start items-stretch gap-[clamp(1.5rem,2vw,2rem)]">
                        <div className="flex-1 self-stretch px-5 py-6 bg-white rounded-xl shadow-[0_4px_6px_-1px_rgba(0,0,0,0.10),0_2px_4px_-2px_rgba(0,0,0,0.10)] inline-flex flex-col justify-start items-start gap-6">
                            <div className="self-stretch inline-flex justify-start items-center gap-3">
                                <div className="size-8 relative overflow-hidden flex items-center justify-center">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                                            fill="#bed000"
                                        />
                                    </svg>
                                </div>
                                <div className="flex-1 justify-start text-[#393939] text-2xl font-semibold font-['Montserrat'] leading-[28.80px]">
                                    Avis à partager
                                </div>
                            </div>

                            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#e4e4e4]"></div>

                            <div className="self-stretch flex-1 inline-flex justify-start items-start gap-10">
                                <div className="flex-1 self-stretch inline-flex flex-col justify-start items-start gap-6">
                                    <TemplateSelector
                                        selectedTemplate={state.selectedTemplate}
                                        onTemplateChange={template =>
                                            setState({ ...state, selectedTemplate: template })
                                        }
                                        templateProps={templateProps}
                                    />

                                    <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#e4e4e4]"></div>

                                    <TextEditor
                                        reviewerName={state.reviewerName}
                                        reviewText={state.reviewText}
                                        onReviewerNameChange={name => {
                                            setState({ ...state, reviewerName: name });
                                            setIsNameModified(true);
                                        }}
                                        onReviewTextChange={text =>
                                            setState({ ...state, reviewText: text })
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="min-w-[380px] max-w-[520px] w-[35%] px-5 py-6 bg-white rounded-xl shadow-[0_4px_6px_-1px_rgba(0,0,0,0.10),0_2px_4px_-2px_rgba(0,0,0,0.10)] inline-flex flex-col justify-start items-start gap-6">
                            <div className="self-stretch inline-flex justify-start items-center gap-3">
                                <div className="size-8 relative overflow-hidden flex items-center justify-center">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                                            fill="#bed000"
                                        />
                                    </svg>
                                </div>
                                <div className="flex-1 justify-start text-[#393939] text-2xl font-semibold font-['Montserrat'] leading-[28.80px]">
                                    Aperçu
                                </div>
                            </div>

                            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#e4e4e4]"></div>

                            <div className="self-stretch flex-1 flex flex-col justify-between items-start">
                                <div className="w-full flex flex-col justify-start items-start gap-2">
                                    <div className="self-stretch flex flex-col justify-start items-start gap-6">
                                        <div className="w-full flex flex-col justify-start items-center gap-6">
                                            <div
                                                ref={previewContainerRef}
                                                className="w-full max-w-[min(90%,450px)] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.10),0_2px_4px_-2px_rgba(0,0,0,0.10)]"
                                                style={{
                                                    aspectRatio: '1/1',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    boxShadow: '0px 2px 4px -2px rgba(0,0,0,0.10)',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '1080px',
                                                        height: '1080px',
                                                        transform: `scale(${previewScale})`,
                                                        transformOrigin: 'top left',
                                                        transition: 'transform 0.2s ease-out',
                                                    }}
                                                >
                                                    {TemplateComponent && (
                                                        <TemplateComponent {...templateProps} />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="justify-center text-[#525254] text-[10px] font-normal font-['Montserrat'] leading-3">
                                                Format 1080 x 1080 pixels
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="w-full flex flex-col justify-start items-start gap-3">
                                    <div className="self-stretch inline-flex justify-between items-center">
                                        <div className="justify-center text-[#525254] text-[13px] font-semibold font-['Montserrat'] leading-none">
                                            Afficher le logo
                                        </div>
                                        <button
                                            onClick={() =>
                                                setState({ ...state, showLogo: !state.showLogo })
                                            }
                                            className={`w-11 h-6 p-0.5 rounded-xl flex items-center overflow-hidden transition-colors ${
                                                state.showLogo
                                                    ? 'bg-[#bed000] justify-end'
                                                    : 'bg-[#9b9b9b] justify-start'
                                            }`}
                                        >
                                            <div className="size-5 bg-white rounded-full shadow-[0px_1px_2px_0px_rgba(16,24,40,0.06)] shadow-[0px_1px_3px_0px_rgba(16,24,40,0.10)]" />
                                        </button>
                                    </div>

                                    <div className="self-stretch inline-flex justify-between items-center">
                                        <div className="flex-1 justify-center text-[#525254] text-[13px] font-semibold font-['Montserrat'] leading-none">
                                            Afficher la source
                                        </div>
                                        <button
                                            onClick={() =>
                                                setState({
                                                    ...state,
                                                    showSource: !state.showSource,
                                                })
                                            }
                                            className={`w-11 h-6 p-0.5 rounded-xl flex items-center overflow-hidden transition-colors ${
                                                state.showSource
                                                    ? 'bg-[#bed000] justify-end'
                                                    : 'bg-[#9b9b9b] justify-start'
                                            }`}
                                        >
                                            <div className="size-5 bg-white rounded-full shadow-[0px_1px_2px_0px_rgba(16,24,40,0.06)] shadow-[0px_1px_3px_0px_rgba(16,24,40,0.10)]" />
                                        </button>
                                    </div>

                                    <div className="self-stretch inline-flex justify-between items-center">
                                        <div className="flex-1 justify-center text-[#525254] text-[13px] font-semibold font-['Montserrat'] leading-none">
                                            Choix de la couleur
                                        </div>
                                        <ColorPicker
                                            selectedColor={state.color}
                                            onColorChange={color => setState({ ...state, color })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#e4e4e4]"></div>

                    <div className="self-stretch inline-flex justify-center items-start gap-6">
                        <button
                            onClick={() => navigate('/reviews')}
                            className="flex-1 max-w-[clamp(280px,30vw,380px)] h-10 px-5 py-2.5 bg-white rounded-[65px] outline outline-1 outline-offset-[-1px] outline-[#e4e4e4] flex justify-center items-center gap-1 hover:bg-gray-50 transition-colors"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M15.8337 10H4.16699M4.16699 10L10.0003 15.8333M4.16699 10L10.0003 4.16667"
                                    stroke="#393939"
                                    strokeWidth="1.67"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <div className="text-center justify-center text-[#757575] text-[13px] font-semibold font-['Montserrat'] leading-none">
                                Annuler la publication
                            </div>
                        </button>

                        <button
                            onClick={handleGenerate}
                            disabled={isGenerating || !state.reviewerName || !state.reviewText}
                            className={`flex-1 max-w-[clamp(280px,30vw,380px)] h-10 px-5 py-2.5 rounded-[65px] flex justify-center items-center gap-1 transition-all ${
                                isGenerating || !state.reviewerName || !state.reviewText
                                    ? 'bg-gray-300 cursor-not-allowed opacity-50'
                                    : 'bg-[#bed000] hover:shadow-lg'
                            }`}
                        >
                            {isGenerating ? (
                                <>
                                    <img
                                        src={loader}
                                        alt="Chargement..."
                                        className="size-5 animate-spin"
                                    />
                                    <div className="text-center justify-center text-[#393939] text-[13px] font-semibold font-['Montserrat'] leading-none">
                                        Génération en cours...
                                    </div>
                                </>
                            ) : (
                                <>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path
                                            d="M16.6668 5L7.50016 14.1667L3.3335 10"
                                            stroke="#393939"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <div className="text-center justify-center text-[#393939] text-[13px] font-semibold font-['Montserrat'] leading-none">
                                        Créer la publication
                                    </div>
                                </>
                            )}
                        </button>
                    </div>

                    {error && (
                        <div className="self-stretch bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}
                </div>

                {/* Template caché pour la capture */}
                <div
                    ref={templateRef}
                    style={{
                        position: 'absolute',
                        left: '-10000px',
                        top: '0',
                        width: '1080px',
                        height: '1080px',
                        pointerEvents: 'none',
                    }}
                >
                    {TemplateComponent && <TemplateComponent {...templateProps} />}
                </div>
            </div>
        </div>
    );
};

export default ShareReviewPage;
